// src/store/slices/adminSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

// ==========================================
// ASYNC THUNKS
// ==========================================

/**
 * Fetch routes for admin moderation with filtering options
 * GET /api/admin/routes
 */
export const fetchAdminRoutes = createAsyncThunk(
    "admin/fetchRoutes",
    async (filters = {}, { rejectWithValue }) => {
        try {
            // Build query parameters from filters
            const params = {};

            if (filters.page) {
                params.page = filters.page;
            }

            if (filters.limit) {
                params.limit = filters.limit;
            }

            if (filters.status && filters.status.length > 0) {
                // If multiple statuses selected, join them or send as comma-separated
                // Backend should handle comma-separated values or we make multiple requests
                // For now, assuming backend accepts comma-separated
                params.status = filters.status.join(',');
            }

            if (filters.state_code && filters.state_code.length > 0) {
                // Join state codes as comma-separated string
                params.state_code = Array.isArray(filters.state_code)
                    ? filters.state_code.join(',')
                    : filters.state_code;
            }

            if (filters.difficulty && filters.difficulty.length > 0) {
                params.difficulty = filters.difficulty.join(',');
            }

            if (filters.creator) {
                params.creator = filters.creator;
            }

            if (filters.minDistance !== undefined && filters.minDistance !== null) {
                params.minDistance = filters.minDistance;
            }

            if (filters.maxDistance !== undefined && filters.maxDistance !== null) {
                params.maxDistance = filters.maxDistance;
            }

            if (filters.sort) {
                params.sort = filters.sort;
            }

            if (filters.order) {
                params.order = filters.order;
            }

            if (filters.name) {
                // Search by name (if backend supports it)
                params.name = filters.name;
            }

            const response = await api.get("/admin/routes", { params });
            return response; // axios interceptor already returns response.data
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch routes" }
            );
        }
    }
);

/**
 * Fetch detailed route information for moderation
 * GET /api/admin/routes/:id
 */
export const fetchAdminRouteDetails = createAsyncThunk(
    "admin/fetchRouteDetails",
    async (routeId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/admin/routes/${routeId}`);
            return response; // axios interceptor already returns response.data
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch route details" }
            );
        }
    }
);

/**
 * Update route status (approve, reject, hide)
 * PUT /api/admin/routes/:id
 */
export const updateRouteStatus = createAsyncThunk(
    "admin/updateRouteStatus",
    async ({ routeId, status, reason }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/admin/routes/${routeId}`, {
                status,
                reason,
            });
            return response; // axios interceptor already returns response.data
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to update route status" }
            );
        }
    }
);

/**
 * Approve a route
 * POST /api/admin/routes/:id/approve
 */
export const approveRoute = createAsyncThunk(
    "admin/approveRoute",
    async (routeId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/routes/${routeId}/approve`);
            return response; // axios interceptor already returns response.data
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to approve route" }
            );
        }
    }
);

/**
 * Reject a route with a reason
 * POST /api/admin/routes/:id/reject
 */
export const rejectRoute = createAsyncThunk(
    "admin/rejectRoute",
    async ({ routeId, rejection_reason }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/routes/${routeId}/reject`, {
                rejection_reason,
            });
            return response; // axios interceptor already returns response.data
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to reject route" }
            );
        }
    }
);

/**
 * Edit route details
 * PUT /api/admin/routes/:id
 */
export const editRoute = createAsyncThunk(
    "admin/editRoute",
    async ({ routeId, updates }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/admin/routes/${routeId}`, updates);
            return response; // axios interceptor already returns response.data
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to edit route" }
            );
        }
    }
);

/**
 * Hide a route
 * POST /api/admin/routes/:id/hide
 */
export const hideRoute = createAsyncThunk(
    "admin/hideRoute",
    async ({ routeId, rejection_reason }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/routes/${routeId}/hide`, {
                rejection_reason,
            });
            return response; // axios interceptor already returns response.data
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to hide route" }
            );
        }
    }
);

/**
 * Unhide a route
 * POST /api/admin/routes/:id/unhide
 */
export const unhideRoute = createAsyncThunk(
    "admin/unhideRoute",
    async (routeId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/routes/${routeId}/unhide`);
            return response; // axios interceptor already returns response.data
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to unhide route" }
            );
        }
    }
);

/**
 * Delete a route
 * DELETE /api/admin/routes/:id
 */
export const deleteRoute = createAsyncThunk(
    "admin/deleteRoute",
    async (routeId, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/admin/routes/${routeId}`);
            return { ...response, routeId }; // Include routeId for removal from state
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to delete route" }
            );
        }
    }
);

/**
 * Create a new route (admin only)
 * POST /api/admin/routes
 */
export const createAdminRoute = createAsyncThunk(
    "admin/createRoute",
    async (formData, { rejectWithValue }) => {
        try {
            const response = await api.post("/admin/routes", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            return response; // axios interceptor already returns response.data
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to create route" }
            );
        }
    }
);

// ==========================================
// USERS MANAGEMENT THUNKS
// ==========================================

/**
 * Suspend a user
 * POST /api/admin/users/:id/suspend
 */
export const suspendUser = createAsyncThunk(
    "admin/suspendUser",
    async ({ userId, reason, duration_days }, { rejectWithValue }) => {
        try {
            const body = { reason };
            if (duration_days) {
                body.duration_days = duration_days;
            }
            const response = await api.post(`/admin/users/${userId}/suspend`, body);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to suspend user" }
            );
        }
    }
);

/**
 * Unsuspend a user
 * POST /api/admin/users/:id/unsuspend
 */
export const unsuspendUser = createAsyncThunk(
    "admin/unsuspendUser",
    async (userId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/users/${userId}/unsuspend`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to unsuspend user" }
            );
        }
    }
);

/**
 * Fetch users list for admin with pagination, search and sorting
 * GET /api/admin/users
 */
export const fetchAdminUsers = createAsyncThunk(
    "admin/fetchUsers",
    async (filters = {}, { rejectWithValue }) => {
        try {
            const params = {};

            if (filters.page) {
                params.page = filters.page;
            }

            if (filters.limit) {
                params.limit = filters.limit;
            }

            if (filters.search) {
                params.search = filters.search;
            }

            if (filters.search_name) {
                params.search_name = filters.search_name;
            }

            if (filters.search_username) {
                params.search_username = filters.search_username;
            }

            if (filters.search_email) {
                params.search_email = filters.search_email;
            }

            if (filters.sort) {
                params.sort = filters.sort;
            }

            if (filters.order) {
                params.order = filters.order;
            }

            if (filters.role) {
                params.role = filters.role;
            }

            const response = await api.get("/admin/users", { params });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch users" }
            );
        }
    }
);

/**
 * Fetch dashboard statistics
 * GET /api/admin/stats
 */
export const fetchAdminStats = createAsyncThunk(
    "admin/fetchStats",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get("/admin/stats");
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch dashboard statistics" }
            );
        }
    }
);

// ==========================================
// ORGANIZER APPLICATIONS THUNKS
// ==========================================

/**
 * Fetch organizer applications list
 * GET /api/admin/organizers
 */
export const fetchOrganizerApplications = createAsyncThunk(
    "admin/fetchOrganizerApplications",
    async (filters = {}, { rejectWithValue }) => {
        try {
            const params = {};
            if (filters.status) params.status = filters.status;
            if (filters.search_name) params.search_name = filters.search_name;
            if (filters.search_email) params.search_email = filters.search_email;
            if (filters.search_city) params.search_city = filters.search_city;
            if (filters.sort) params.sort = filters.sort;
            if (filters.order) params.order = filters.order;
            if (filters.page) params.page = filters.page;
            if (filters.limit) params.limit = filters.limit;

            const response = await api.get("/admin/organizers", { params });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch organizer applications" }
            );
        }
    }
);

/**
 * Fetch organizer application details
 * GET /api/admin/organizers/:id
 */
export const fetchOrganizerApplicationDetails = createAsyncThunk(
    "admin/fetchOrganizerDetails",
    async (organizerId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/admin/organizers/${organizerId}`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch organizer details" }
            );
        }
    }
);

/**
 * Approve organizer application
 * POST /api/admin/organizers/:id/approve
 */
export const approveOrganizerApplication = createAsyncThunk(
    "admin/approveOrganizer",
    async (organizerId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/organizers/${organizerId}/approve`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to approve organizer" }
            );
        }
    }
);

/**
 * Reject organizer application
 * POST /api/admin/organizers/:id/reject
 */
export const rejectOrganizerApplication = createAsyncThunk(
    "admin/rejectOrganizer",
    async ({ organizerId, rejection_reason }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/organizers/${organizerId}/reject`, {
                rejection_reason,
            });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to reject organizer" }
            );
        }
    }
);

/**
 * Suspend an organizer
 * POST /api/admin/organizers/:id/suspend
 */
export const suspendOrganizer = createAsyncThunk(
    "admin/suspendOrganizer",
    async ({ organizerId, reason, duration_days }, { rejectWithValue }) => {
        try {
            const body = { reason };
            if (duration_days) {
                body.duration_days = duration_days;
            }
            const response = await api.post(`/admin/organizers/${organizerId}/suspend`, body);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to suspend organizer" }
            );
        }
    }
);

/**
 * Unsuspend an organizer
 * POST /api/admin/organizers/:id/unsuspend
 */
export const unsuspendOrganizer = createAsyncThunk(
    "admin/unsuspendOrganizer",
    async (organizerId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/organizers/${organizerId}/unsuspend`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to unsuspend organizer" }
            );
        }
    }
);

// ==========================================
// SPONSOR APPLICATIONS THUNKS
// ==========================================

/**
 * Fetch sponsor applications list
 * GET /api/admin/sponsors
 */
export const fetchSponsorApplications = createAsyncThunk(
    "admin/fetchSponsorApplications",
    async (filters = {}, { rejectWithValue }) => {
        try {
            const params = {};
            if (filters.status) params.status = filters.status;
            if (filters.search_company) params.search_company = filters.search_company;
            if (filters.search_email) params.search_email = filters.search_email;
            if (filters.search_zone) params.search_zone = filters.search_zone;
            if (filters.sort) params.sort = filters.sort;
            if (filters.order) params.order = filters.order;
            if (filters.page) params.page = filters.page;
            if (filters.limit) params.limit = filters.limit;

            const response = await api.get("/admin/sponsors", { params });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch sponsor applications" }
            );
        }
    }
);

/**
 * Fetch sponsor application details
 * GET /api/admin/sponsors/:id
 */
export const fetchSponsorApplicationDetails = createAsyncThunk(
    "admin/fetchSponsorDetails",
    async (sponsorId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/admin/sponsors/${sponsorId}`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch sponsor details" }
            );
        }
    }
);

/**
 * Approve sponsor application
 * POST /api/admin/sponsors/:id/approve
 */
export const approveSponsorApplication = createAsyncThunk(
    "admin/approveSponsor",
    async (sponsorId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/sponsors/${sponsorId}/approve`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to approve sponsor" }
            );
        }
    }
);

/**
 * Reject sponsor application
 * POST /api/admin/sponsors/:id/reject
 */
export const rejectSponsorApplication = createAsyncThunk(
    "admin/rejectSponsor",
    async ({ sponsorId, rejection_reason }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/sponsors/${sponsorId}/reject`, {
                rejection_reason,
            });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to reject sponsor" }
            );
        }
    }
);

/**
 * Suspend a sponsor
 * POST /api/admin/sponsors/:id/suspend
 */
export const suspendSponsor = createAsyncThunk(
    "admin/suspendSponsor",
    async ({ sponsorId, reason, duration_days }, { rejectWithValue }) => {
        try {
            const body = { reason };
            if (duration_days) {
                body.duration_days = duration_days;
            }
            const response = await api.post(`/admin/sponsors/${sponsorId}/suspend`, body);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to suspend sponsor" }
            );
        }
    }
);

/**
 * Unsuspend a sponsor
 * POST /api/admin/sponsors/:id/unsuspend
 */
export const unsuspendSponsor = createAsyncThunk(
    "admin/unsuspendSponsor",
    async (sponsorId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/sponsors/${sponsorId}/unsuspend`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to unsuspend sponsor" }
            );
        }
    }
);

// ==========================================
// EVENT MANAGEMENT THUNKS
// ==========================================

/**
 * Fetch all events for admin with filters
 * GET /api/admin/events
 */
export const fetchAdminEvents = createAsyncThunk(
    "admin/fetchEvents",
    async (filters = {}, { rejectWithValue }) => {
        try {
            const params = {};
            if (filters.page) params.page = filters.page;
            if (filters.limit) params.limit = filters.limit;
            if (filters.status) params.status = filters.status;
            if (filters.event_type) params.event_type = filters.event_type;
            if (filters.component) params.component = filters.component;
            if (filters.organizer) params.organizer = filters.organizer;
            if (filters.search) params.search = filters.search;
            if (filters.city) params.city = filters.city;
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;
            if (filters.sort) params.sort = filters.sort;

            const response = await api.get("/admin/events", { params });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch events" }
            );
        }
    }
);

/**
 * Move event to sponsor_listed status
 * POST /api/admin/events/:id/sponsor-list
 */
export const sponsorListEvent = createAsyncThunk(
    "admin/sponsorListEvent",
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/events/${eventId}/sponsor-list`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to move event to sponsor listing" }
            );
        }
    }
);

/**
 * Publish an event
 * POST /api/admin/events/:id/publish
 */
export const publishAdminEvent = createAsyncThunk(
    "admin/publishEvent",
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/events/${eventId}/publish`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to publish event" }
            );
        }
    }
);

/**
 * Archive an event
 * POST /api/admin/events/:id/archive
 */
export const archiveAdminEvent = createAsyncThunk(
    "admin/archiveEvent",
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/events/${eventId}/archive`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to archive event" }
            );
        }
    }
);

/**
 * Hide an event
 * POST /api/admin/events/:id/hide
 */
export const hideAdminEvent = createAsyncThunk(
    "admin/hideEvent",
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/events/${eventId}/hide`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to hide event" }
            );
        }
    }
);

/**
 * Unhide an event
 * POST /api/admin/events/:id/unhide
 */
export const unhideAdminEvent = createAsyncThunk(
    "admin/unhideEvent",
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/events/${eventId}/unhide`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to unhide event" }
            );
        }
    }
);

/**
 * Transition event status (generic handler for backtracks and transitions)
 * POST /api/admin/events/:id/transition
 */
export const transitionAdminEvent = createAsyncThunk(
    "admin/transitionEvent",
    async ({ eventId, status, reason }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/events/${eventId}/transition`, { status, reason });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to transition event status" }
            );
        }
    }
);

/**
 * Edit any event (admin override)
 * PUT /api/admin/events/:id
 */
export const editAdminEvent = createAsyncThunk(
    "admin/editEvent",
    async ({ eventId, updates }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/admin/events/${eventId}`, updates);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to edit event" }
            );
        }
    }
);

/**
 * Fetch sponsorship interests for an event
 * GET /api/admin/events/:id/sponsorships
 */
export const fetchSponsorshipInterests = createAsyncThunk(
    "admin/fetchSponsorshipInterests",
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/admin/events/${eventId}/sponsorships`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch sponsorship interests" }
            );
        }
    }
);

/**
 * Assign a sponsor to a component
 * POST /api/admin/events/:id/components/:componentId/assign
 */
export const assignComponentSponsor = createAsyncThunk(
    "admin/assignComponentSponsor",
    async ({ eventId, componentId, sponsor_id }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/events/${eventId}/components/${componentId}/assign`, {
                sponsor_id
            });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to assign sponsor" }
            );
        }
    }
);

/**
 * Unassign a sponsor from a component
 * DELETE /api/admin/events/:id/components/:componentId/assign
 */
export const unassignComponentSponsorThunk = createAsyncThunk(
    "admin/unassignComponentSponsor",
    async ({ eventId, componentId }, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/admin/events/${eventId}/components/${componentId}/assign`);
            return { ...response, componentId };
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to unassign sponsor" }
            );
        }
    }
);

/**
 * Review a sponsorship interest
 * POST /api/admin/sponsorships/:id/review
 */
export const reviewSponsorshipInterest = createAsyncThunk(
    "admin/reviewSponsorshipInterest",
    async ({ interestId, admin_notes }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/sponsorships/${interestId}/review`, {
                admin_notes,
            });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to review sponsorship interest" }
            );
        }
    }
);

/**
 * Update sponsorship interest status to any valid status
 * PUT /api/admin/sponsorships/:id
 */
export const updateInterestStatus = createAsyncThunk(
    "admin/updateInterestStatus",
    async ({ interestId, status }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/admin/sponsorships/${interestId}`, {
                status,
            });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to update interest status" }
            );
        }
    }
);


// ==========================================
// MODERATOR MANAGEMENT THUNKS
// ==========================================

/**
 * Fetch all moderators
 * GET /api/admin/moderators
 */
export const fetchModerators = createAsyncThunk(
    "admin/fetchModerators",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get("/admin/moderators");
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch moderators" }
            );
        }
    }
);

/**
 * Promote a user to moderator
 * POST /api/admin/users/:id/make-moderator
 */
export const promoteToModerator = createAsyncThunk(
    "admin/promoteToModerator",
    async ({ userId, permissions }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/users/${userId}/make-moderator`, {
                permissions,
            });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to promote user to moderator" }
            );
        }
    }
);

/**
 * Update moderator permissions
 * PUT /api/admin/users/:id/permissions
 */
export const updateModeratorPermissions = createAsyncThunk(
    "admin/updateModeratorPermissions",
    async ({ userId, permissions }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/admin/users/${userId}/permissions`, {
                permissions,
            });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to update moderator permissions" }
            );
        }
    }
);

/**
 * Revoke moderator role
 * POST /api/admin/users/:id/revoke-moderator
 */
export const revokeModeratorRole = createAsyncThunk(
    "admin/revokeModeratorRole",
    async (userId, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/users/${userId}/revoke-moderator`);
            return { ...response, userId };
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to revoke moderator role" }
            );
        }
    }
);


// ==========================================
// ORGANIZER EDIT OVERRIDE THUNKS
// ==========================================

/**
 * Fetch events for a specific organizer (admin view)
 * GET /api/admin/events?organizer_id=:organizerId
 */
export const fetchOrganizerEventsForAdmin = createAsyncThunk(
    "admin/fetchOrganizerEventsForAdmin",
    async (organizerId, { rejectWithValue }) => {
        try {
            const response = await api.get("/admin/events", {
                params: { organizer_id: organizerId, limit: 50 }
            });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch organizer events" }
            );
        }
    }
);

/**
 * Grant edit override to organizer for a specific event
 * POST /api/admin/organizers/:id/events/:eventId/grant-edit
 */
export const grantEditOverride = createAsyncThunk(
    "admin/grantEditOverride",
    async ({ organizerId, eventId }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/organizers/${organizerId}/events/${eventId}/grant-edit`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to grant edit override" }
            );
        }
    }
);

/**
 * Revoke edit override from organizer for a specific event
 * POST /api/admin/organizers/:id/events/:eventId/revoke-edit
 */
export const revokeEditOverride = createAsyncThunk(
    "admin/revokeEditOverride",
    async ({ organizerId, eventId }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/admin/organizers/${organizerId}/events/${eventId}/revoke-edit`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to revoke edit override" }
            );
        }
    }
);

// ==========================================
// AUDIT LOGS THUNKS
// ==========================================

/**
 * Fetch audit logs
 * GET /api/admin/audit-logs
 */
export const fetchAuditLogs = createAsyncThunk(
    "admin/fetchAuditLogs",
    async (filters = {}, { rejectWithValue }) => {
        try {
            const params = { ...filters };
            const response = await api.get("/admin/audit-logs", { params });
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Failed to fetch audit logs" }
            );
        }
    }
);

/**
 * Fetch a single event by ID (for audit log deep-link navigation)
 * GET /api/admin/events/:id
 */
export const fetchAdminEventById = createAsyncThunk(
    "admin/fetchEventById",
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/admin/events/${eventId}`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "Event not found" }
            );
        }
    }
);

/**
 * Fetch a single user by ID (for audit log deep-link navigation)
 * GET /api/admin/users/:id
 */
export const fetchAdminUserById = createAsyncThunk(
    "admin/fetchUserById",
    async (userId, { rejectWithValue }) => {
        try {
            const response = await api.get(`/admin/users/${userId}`);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { message: "User not found" }
            );
        }
    }
);

// ==========================================
// SLICE DEFINITION
// ==========================================

const adminSlice = createSlice({
    name: "admin",
    initialState: {
        // Routes list for admin management
        routes: [],
        totalRoutes: 0,
        routesPage: 1,
        routesLimit: 20,
        routesTotalPages: 0,

        // Current route details being viewed/edited
        currentRoute: null,

        // Loading states
        isLoading: false,
        isUpdating: false,
        isLoadingDetails: false,
        submitLoading: false,

        // Error handling
        error: null,
        updateError: null,

        // Current filters applied (for reference)
        appliedFilters: {},

        // Users list for admin management
        users: [],
        totalUsers: 0,
        usersPage: 1,
        usersLimit: 20,
        usersTotalPages: 0,
        isLoadingUsers: false,
        usersError: null,

        // Dashboard statistics
        stats: null,
        isLoadingStats: false,
        statsError: null,

        // Organizer applications management
        organizerApplications: [],
        totalOrganizerApplications: 0,
        organizersPage: 1,
        organizersLimit: 20,
        organizersTotalPages: 0,
        currentOrganizerApplication: null,
        isLoadingOrganizers: false,
        organizersError: null,

        // Sponsor applications management
        sponsorApplications: [],
        totalSponsorApplications: 0,
        sponsorsPage: 1,
        sponsorsLimit: 20,
        sponsorsTotalPages: 0,
        currentSponsorApplication: null,
        isLoadingSponsors: false,
        sponsorsError: null,

        // Event management
        events: [],
        totalEvents: 0,
        eventsPage: 1,
        eventsLimit: 20,
        eventsTotalPages: 0,
        componentCounts: [],
        isLoadingEvents: false,
        eventsError: null,

        // Sponsorship Assignments
        sponsorshipData: null,
        isLoadingSponsorships: false,
        sponsorshipsError: null,

        // Moderator management
        moderators: [],
        isLoadingModerators: false,
        moderatorsError: null,

        // Audit Logs
        auditLogs: [],
        auditLogsHasNext: false,
        auditLogsCursor: null,
        isLoadingAuditLogs: false,
        auditLogsError: null,

        // Audit nav — entity fetched by ID for deep-link
        auditNavEntity: null,
        isLoadingAuditNav: false,
        auditNavError: null,
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
            state.updateError = null;
        },
        clearCurrentRoute: (state) => {
            state.currentRoute = null;
        },
        setAppliedFilters: (state, action) => {
            state.appliedFilters = action.payload;
        },
        clearAuditNav: (state) => {
            state.auditNavEntity = null;
            state.auditNavError = null;
            state.isLoadingAuditNav = false;
            state.currentOrganizerApplication = null;
            state.currentSponsorApplication = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ==========================================
            // FETCH ADMIN ROUTES
            // ==========================================
            .addCase(fetchAdminRoutes.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAdminRoutes.fulfilled, (state, action) => {
                state.isLoading = false;
                state.routes = action.payload.data?.routes || [];
                const pagination = action.payload.data?.pagination;
                if (pagination) {
                    state.totalRoutes = pagination.totalDocuments || 0;
                    state.routesPage = pagination.currentPage || 1;
                    state.routesTotalPages = pagination.totalPages || 0;
                } else {
                    state.totalRoutes = action.payload.count || state.routes.length;
                }
            })
            .addCase(fetchAdminRoutes.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to fetch routes";
            })

            // ==========================================
            // FETCH ROUTE DETAILS
            // ==========================================
            .addCase(fetchAdminRouteDetails.pending, (state) => {
                state.isLoadingDetails = true;
                state.error = null;
            })
            .addCase(fetchAdminRouteDetails.fulfilled, (state, action) => {
                state.isLoadingDetails = false;
                state.currentRoute = action.payload.data?.route || null;
            })
            .addCase(fetchAdminRouteDetails.rejected, (state, action) => {
                state.isLoadingDetails = false;
                state.error = action.payload?.message || "Failed to fetch route details";
            })

            // ==========================================
            // UPDATE ROUTE STATUS
            // ==========================================
            .addCase(updateRouteStatus.pending, (state) => {
                state.isUpdating = true;
                state.updateError = null;
            })
            .addCase(updateRouteStatus.fulfilled, (state, action) => {
                state.isUpdating = false;

                // Update the route in the list if it exists
                const updatedRoute = action.payload.data?.route;
                if (updatedRoute) {
                    const index = state.routes.findIndex((r) => r._id === updatedRoute._id);
                    if (index !== -1) {
                        state.routes[index] = updatedRoute;
                    }

                    // Update current route if it's the same one
                    if (state.currentRoute?._id === updatedRoute._id) {
                        state.currentRoute = updatedRoute;
                    }
                }
            })
            .addCase(updateRouteStatus.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to update route status";
            })

            // ==========================================
            // APPROVE ROUTE
            // ==========================================
            .addCase(approveRoute.pending, (state) => {
                state.isUpdating = true;
                state.updateError = null;
            })
            .addCase(approveRoute.fulfilled, (state, action) => {
                state.isUpdating = false;

                // Update the route in the list
                const updatedRoute = action.payload.data?.route;
                if (updatedRoute) {
                    const index = state.routes.findIndex((r) => r._id === updatedRoute._id);
                    if (index !== -1) {
                        state.routes[index] = updatedRoute;
                    }

                    // Update current route if it's the same one
                    if (state.currentRoute?._id === updatedRoute._id) {
                        state.currentRoute = updatedRoute;
                    }
                }
            })
            .addCase(approveRoute.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to approve route";
            })

            // ==========================================
            // REJECT ROUTE
            // ==========================================
            .addCase(rejectRoute.pending, (state) => {
                state.isUpdating = true;
                state.updateError = null;
            })
            .addCase(rejectRoute.fulfilled, (state, action) => {
                state.isUpdating = false;

                // Update the route in the list
                const updatedRoute = action.payload.data?.route;
                if (updatedRoute) {
                    const index = state.routes.findIndex((r) => r._id === updatedRoute._id);
                    if (index !== -1) {
                        state.routes[index] = updatedRoute;
                    }

                    // Update current route if it's the same one
                    if (state.currentRoute?._id === updatedRoute._id) {
                        state.currentRoute = updatedRoute;
                    }
                }
            })
            .addCase(rejectRoute.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to reject route";
            })

            // ==========================================
            // EDIT ROUTE
            // ==========================================
            .addCase(editRoute.pending, (state) => {
                state.isUpdating = true;
                state.updateError = null;
            })
            .addCase(editRoute.fulfilled, (state, action) => {
                state.isUpdating = false;

                // Update the route in the list
                const updatedRoute = action.payload.data?.route;
                if (updatedRoute) {
                    const index = state.routes.findIndex((r) => r._id === updatedRoute._id);
                    if (index !== -1) {
                        state.routes[index] = updatedRoute;
                    }

                    // Update current route if it's the same one
                    if (state.currentRoute?._id === updatedRoute._id) {
                        state.currentRoute = updatedRoute;
                    }
                }
            })
            .addCase(editRoute.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to edit route";
            })

            // ==========================================
            // HIDE ROUTE
            // ==========================================
            .addCase(hideRoute.pending, (state) => {
                state.isUpdating = true;
                state.updateError = null;
            })
            .addCase(hideRoute.fulfilled, (state, action) => {
                state.isUpdating = false;

                // Update the route in the list
                const updatedRoute = action.payload.data?.route;
                if (updatedRoute) {
                    const index = state.routes.findIndex((r) => r._id === updatedRoute._id);
                    if (index !== -1) {
                        state.routes[index] = updatedRoute;
                    }

                    // Update current route if it's the same one
                    if (state.currentRoute?._id === updatedRoute._id) {
                        state.currentRoute = updatedRoute;
                    }
                }
            })
            .addCase(hideRoute.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to hide route";
            })

            // ==========================================
            // UNHIDE ROUTE
            // ==========================================
            .addCase(unhideRoute.pending, (state) => {
                state.isUpdating = true;
                state.updateError = null;
            })
            .addCase(unhideRoute.fulfilled, (state, action) => {
                state.isUpdating = false;

                // Update the route in the list
                const updatedRoute = action.payload.data?.route;
                if (updatedRoute) {
                    const index = state.routes.findIndex((r) => r._id === updatedRoute._id);
                    if (index !== -1) {
                        state.routes[index] = updatedRoute;
                    }

                    // Update current route if it's the same one
                    if (state.currentRoute?._id === updatedRoute._id) {
                        state.currentRoute = updatedRoute;
                    }
                }
            })
            .addCase(unhideRoute.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to unhide route";
            })

            // ==========================================
            // DELETE ROUTE
            // ==========================================
            .addCase(deleteRoute.pending, (state) => {
                state.isUpdating = true;
                state.updateError = null;
            })
            .addCase(deleteRoute.fulfilled, (state, action) => {
                state.isUpdating = false;

                // Remove the route from the list
                const routeId = action.payload.routeId;
                state.routes = state.routes.filter((r) => r._id !== routeId);
                state.totalRoutes = state.totalRoutes - 1;

                // Clear current route if it's the deleted one
                if (state.currentRoute?._id === routeId) {
                    state.currentRoute = null;
                }
            })
            .addCase(deleteRoute.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to delete route";
            })

            // ==========================================
            // CREATE ADMIN ROUTE
            // ==========================================
            .addCase(createAdminRoute.pending, (state) => {
                state.submitLoading = true;
                state.error = null;
            })
            .addCase(createAdminRoute.fulfilled, (state, action) => {
                state.submitLoading = false;

                // Add the new route to the list
                const newRoute = action.payload.data?.route;
                if (newRoute) {
                    state.routes.unshift(newRoute); // Add to beginning of list
                    state.totalRoutes = state.totalRoutes + 1;
                }
            })
            .addCase(createAdminRoute.rejected, (state, action) => {
                state.submitLoading = false;
                state.error = action.payload?.message || "Failed to create route";
            })

            // ==========================================
            // FETCH ADMIN USERS
            // ==========================================
            .addCase(fetchAdminUsers.pending, (state) => {
                state.isLoadingUsers = true;
                state.usersError = null;
            })
            .addCase(fetchAdminUsers.fulfilled, (state, action) => {
                state.isLoadingUsers = false;
                state.users = action.payload.data?.users || [];
                const pagination = action.payload.data?.pagination || {};
                state.totalUsers = pagination.totalDocuments || 0;
                state.usersPage = pagination.currentPage || 1;
                state.usersTotalPages = pagination.totalPages || 0;
            })
            .addCase(fetchAdminUsers.rejected, (state, action) => {
                state.isLoadingUsers = false;
                state.usersError = action.payload?.message || "Failed to fetch users";
            })

            // ==========================================
            // SUSPEND USER
            // ==========================================
            .addCase(suspendUser.pending, (state) => {
                state.isUpdating = true;
                state.usersError = null;
            })
            .addCase(suspendUser.fulfilled, (state, action) => {
                state.isUpdating = false;
                const updatedUser = action.payload.data?.user;
                if (updatedUser) {
                    const index = state.users.findIndex((u) => u._id === updatedUser._id);
                    if (index !== -1) {
                        state.users[index] = updatedUser;
                    }
                }
            })
            .addCase(suspendUser.rejected, (state, action) => {
                state.isUpdating = false;
                state.usersError = action.payload?.message || "Failed to suspend user";
            })

            // ==========================================
            // UNSUSPEND USER
            // ==========================================
            .addCase(unsuspendUser.pending, (state) => {
                state.isUpdating = true;
                state.usersError = null;
            })
            .addCase(unsuspendUser.fulfilled, (state, action) => {
                state.isUpdating = false;
                const updatedUser = action.payload.data?.user;
                if (updatedUser) {
                    const index = state.users.findIndex((u) => u._id === updatedUser._id);
                    if (index !== -1) {
                        state.users[index] = updatedUser;
                    }
                }
            })
            .addCase(unsuspendUser.rejected, (state, action) => {
                state.isUpdating = false;
                state.usersError = action.payload?.message || "Failed to unsuspend user";
            })

            // ==========================================
            // FETCH ADMIN STATS
            // ==========================================
            .addCase(fetchAdminStats.pending, (state) => {
                state.isLoadingStats = true;
                state.statsError = null;
            })
            .addCase(fetchAdminStats.fulfilled, (state, action) => {
                state.isLoadingStats = false;
                state.stats = action.payload.data?.stats || null;
            })
            .addCase(fetchAdminStats.rejected, (state, action) => {
                state.isLoadingStats = false;
                state.statsError = action.payload?.message || "Failed to fetch dashboard statistics";
            })

            // ==========================================
            // ORGANIZER APPLICATIONS
            // ==========================================
            .addCase(fetchOrganizerApplications.pending, (state) => {
                state.isLoadingOrganizers = true;
                state.organizersError = null;
            })
            .addCase(fetchOrganizerApplications.fulfilled, (state, action) => {
                state.isLoadingOrganizers = false;
                state.organizerApplications = action.payload.data?.applications || action.payload.data?.organizers || [];
                const pagination = action.payload.data?.pagination;
                if (pagination) {
                    state.totalOrganizerApplications = pagination.totalDocuments || 0;
                    state.organizersPage = pagination.currentPage || 1;
                    state.organizersTotalPages = pagination.totalPages || 0;
                } else {
                    state.totalOrganizerApplications = action.payload.count || state.organizerApplications.length;
                }
            })
            .addCase(fetchOrganizerApplications.rejected, (state, action) => {
                state.isLoadingOrganizers = false;
                state.organizersError = action.payload?.message || "Failed to fetch organizer applications";
            })
            .addCase(fetchOrganizerApplicationDetails.pending, (state) => {
                state.isLoadingOrganizers = true;
            })
            .addCase(fetchOrganizerApplicationDetails.fulfilled, (state, action) => {
                state.isLoadingOrganizers = false;
                state.currentOrganizerApplication = action.payload.data?.application || action.payload.data?.organizer || null;
            })
            .addCase(fetchOrganizerApplicationDetails.rejected, (state, action) => {
                state.isLoadingOrganizers = false;
                state.organizersError = action.payload?.message || "Failed to fetch details";
            })
            .addCase(approveOrganizerApplication.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(approveOrganizerApplication.fulfilled, (state, action) => {
                state.isUpdating = false;
                // Backend returns data: { application: {...} } or { organizer: {...} } depending on implementation
                // Checking controller: returns data: { organizer_id, email, temp_password } but we might refetch list usually
                // But let's check what it returns
                // Controller says: data: { organizer_id, ... }
                // We should probably just refetch the list or update the specific item status locally

                // For now, let's assume we refresh the list via the component's useEffect or we just update the status locally if we have the ID.
                // The component calls dispatch(fetchOrganizerApplications) after success, so this state update might be redundant but harmless if matching fails.

                // However, matching logic looked for updatedOrg._id.
                // If the backend response doesn't contain the full organizer object, this won't work.
                // But the critical fix here is for the fetch list.
            })
            .addCase(approveOrganizerApplication.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to approve organizer";
            })
            .addCase(rejectOrganizerApplication.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(rejectOrganizerApplication.fulfilled, (state, action) => {
                state.isUpdating = false;
                // Same here
            })
            .addCase(rejectOrganizerApplication.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to reject organizer";
            })
            .addCase(suspendOrganizer.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(suspendOrganizer.fulfilled, (state, action) => {
                state.isUpdating = false;
            })
            .addCase(suspendOrganizer.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to suspend organizer";
            })
            .addCase(unsuspendOrganizer.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(unsuspendOrganizer.fulfilled, (state, action) => {
                state.isUpdating = false;
            })
            .addCase(unsuspendOrganizer.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to unsuspend organizer";
            })

            // ==========================================
            // SPONSOR APPLICATIONS
            // ==========================================
            .addCase(fetchSponsorApplications.pending, (state) => {
                state.isLoadingSponsors = true;
                state.sponsorsError = null;
            })
            .addCase(fetchSponsorApplications.fulfilled, (state, action) => {
                state.isLoadingSponsors = false;
                state.sponsorApplications = action.payload.data?.applications || action.payload.data?.sponsors || [];
                const pagination = action.payload.data?.pagination;
                if (pagination) {
                    state.totalSponsorApplications = pagination.totalDocuments || 0;
                    state.sponsorsPage = pagination.currentPage || 1;
                    state.sponsorsTotalPages = pagination.totalPages || 0;
                } else {
                    state.totalSponsorApplications = action.payload.count || state.sponsorApplications.length;
                }
            })
            .addCase(fetchSponsorApplications.rejected, (state, action) => {
                state.isLoadingSponsors = false;
                state.sponsorsError = action.payload?.message || "Failed to fetch sponsor applications";
            })
            .addCase(fetchSponsorApplicationDetails.pending, (state) => {
                state.isLoadingSponsors = true;
            })
            .addCase(fetchSponsorApplicationDetails.fulfilled, (state, action) => {
                state.isLoadingSponsors = false;
                state.currentSponsorApplication = action.payload.data?.application || action.payload.data?.sponsor || null;
            })
            .addCase(fetchSponsorApplicationDetails.rejected, (state, action) => {
                state.isLoadingSponsors = false;
                state.sponsorsError = action.payload?.message || "Failed to fetch details";
            })
            .addCase(approveSponsorApplication.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(approveSponsorApplication.fulfilled, (state, action) => {
                state.isUpdating = false;
                const updatedSponsor = action.payload.data?.sponsor;
                if (updatedSponsor) {
                    const index = state.sponsorApplications.findIndex(s => s._id === updatedSponsor._id);
                    if (index !== -1) {
                        state.sponsorApplications[index] = updatedSponsor;
                    }
                }
            })
            .addCase(approveSponsorApplication.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to approve sponsor";
            })
            .addCase(rejectSponsorApplication.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(rejectSponsorApplication.fulfilled, (state, action) => {
                state.isUpdating = false;
                const updatedSponsor = action.payload.data?.sponsor;
                if (updatedSponsor) {
                    const index = state.sponsorApplications.findIndex(s => s._id === updatedSponsor._id);
                    if (index !== -1) {
                        state.sponsorApplications[index] = updatedSponsor;
                    }
                }
            })
            .addCase(rejectSponsorApplication.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to reject sponsor";
            })
            .addCase(suspendSponsor.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(suspendSponsor.fulfilled, (state, action) => {
                state.isUpdating = false;
            })
            .addCase(suspendSponsor.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to suspend sponsor";
            })
            .addCase(unsuspendSponsor.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(unsuspendSponsor.fulfilled, (state, action) => {
                state.isUpdating = false;
            })
            .addCase(unsuspendSponsor.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to unsuspend sponsor";
            })

            // ==========================================
            // FETCH ADMIN EVENTS
            // ==========================================
            .addCase(fetchAdminEvents.pending, (state) => {
                state.isLoadingEvents = true;
                state.eventsError = null;
            })
            .addCase(fetchAdminEvents.fulfilled, (state, action) => {
                state.isLoadingEvents = false;
                state.events = action.payload.data?.events || [];
                state.componentCounts = action.payload.data?.component_counts || [];
                const pagination = action.payload.data?.pagination;
                if (pagination) {
                    state.totalEvents = pagination.totalDocuments || 0;
                    state.eventsPage = pagination.currentPage || 1;
                    state.eventsTotalPages = pagination.totalPages || 0;
                } else {
                    state.totalEvents = action.payload.count || state.events.length;
                }
            })
            .addCase(fetchAdminEvents.rejected, (state, action) => {
                state.isLoadingEvents = false;
                state.eventsError = action.payload?.message || "Failed to fetch events";
            })

            // ==========================================
            // SPONSOR LIST EVENT
            // ==========================================
            .addCase(sponsorListEvent.pending, (state) => {
                state.isUpdating = true;
                state.updateError = null;
            })
            .addCase(sponsorListEvent.fulfilled, (state, action) => {
                state.isUpdating = false;
                const updatedEvent = action.payload.data?.event;
                if (updatedEvent) {
                    const index = state.events.findIndex((e) => e._id === updatedEvent._id);
                    if (index !== -1) state.events[index] = updatedEvent;
                }
            })
            .addCase(sponsorListEvent.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to sponsor-list event";
            })

            // ==========================================
            // PUBLISH EVENT
            // ==========================================
            .addCase(publishAdminEvent.pending, (state) => {
                state.isUpdating = true;
                state.updateError = null;
            })
            .addCase(publishAdminEvent.fulfilled, (state, action) => {
                state.isUpdating = false;
                const updatedEvent = action.payload.data?.event;
                if (updatedEvent) {
                    const index = state.events.findIndex((e) => e._id === updatedEvent._id);
                    if (index !== -1) state.events[index] = updatedEvent;
                }
            })
            .addCase(publishAdminEvent.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to publish event";
            })

            // ==========================================
            // ARCHIVE EVENT
            // ==========================================
            .addCase(archiveAdminEvent.pending, (state) => {
                state.isUpdating = true;
                state.updateError = null;
            })
            .addCase(archiveAdminEvent.fulfilled, (state, action) => {
                state.isUpdating = false;
                const updatedEvent = action.payload.data?.event;
                if (updatedEvent) {
                    const index = state.events.findIndex((e) => e._id === updatedEvent._id);
                    if (index !== -1) state.events[index] = updatedEvent;
                }
            })
            .addCase(archiveAdminEvent.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to archive event";
            })

            // ==========================================
            // EDIT EVENT
            // ==========================================
            .addCase(editAdminEvent.pending, (state) => {
                state.isUpdating = true;
                state.updateError = null;
            })
            .addCase(editAdminEvent.fulfilled, (state, action) => {
                state.isUpdating = false;
                const updatedEvent = action.payload.data?.event;
                if (updatedEvent) {
                    const index = state.events.findIndex((e) => e._id === updatedEvent._id);
                    if (index !== -1) state.events[index] = updatedEvent;
                }
            })
            .addCase(editAdminEvent.rejected, (state, action) => {
                state.isUpdating = false;
                state.updateError = action.payload?.message || "Failed to edit event";
            })

            // ==========================================
            // FETCH SPONSORSHIP INTERESTS
            // ==========================================
            .addCase(fetchSponsorshipInterests.pending, (state) => {
                state.isLoadingSponsorships = true;
                state.sponsorshipsError = null;
            })
            .addCase(fetchSponsorshipInterests.fulfilled, (state, action) => {
                state.isLoadingSponsorships = false;
                state.sponsorshipData = action.payload.data;
            })
            .addCase(fetchSponsorshipInterests.rejected, (state, action) => {
                state.isLoadingSponsorships = false;
                state.sponsorshipsError = action.payload?.message || "Failed to fetch sponsorship interests";
            })

            // ==========================================
            // ASSIGN COMPONENT SPONSOR
            // ==========================================
            .addCase(assignComponentSponsor.pending, (state) => {
                state.isUpdating = true;
                state.sponsorshipsError = null;
            })
            .addCase(assignComponentSponsor.fulfilled, (state, action) => {
                state.isUpdating = false;
                // We should update the specific component in state.sponsorshipData.component_summary
                const updatedComponent = action.payload.data?.component;
                if (state.sponsorshipData && updatedComponent) {
                    const index = state.sponsorshipData.component_summary.findIndex(c => c.component_id === updatedComponent.id);
                    if (index !== -1) {
                        // Update the component in the list.
                        // Note: The backend response 'component' structure might differ from 'component_summary' structure.
                        // Ideally re-fetch, but for now let's manually patch what we can or rely on re-fetch if simpler.
                        // Re-fetching is safer given the complex summary structure.
                    }
                }
            })
            .addCase(assignComponentSponsor.rejected, (state, action) => {
                state.isUpdating = false;
                state.sponsorshipsError = action.payload?.message || "Failed to assign sponsor";
            })

            // ==========================================
            // UNASSIGN COMPONENT SPONSOR
            // ==========================================
            .addCase(unassignComponentSponsorThunk.pending, (state) => {
                state.isUpdating = true;
                state.sponsorshipsError = null;
            })
            .addCase(unassignComponentSponsorThunk.fulfilled, (state, action) => {
                state.isUpdating = false;
                // Similar to assign, ideally re-fetch or manual update.
            })
            .addCase(unassignComponentSponsorThunk.rejected, (state, action) => {
                state.isUpdating = false;
                state.sponsorshipsError = action.payload?.message || "Failed to unassign sponsor";
            })

            // ==========================================
            // REVIEW SPONSORSHIP INTEREST
            // ==========================================
            .addCase(reviewSponsorshipInterest.pending, (state) => {
                state.isUpdating = true;
                state.sponsorshipsError = null;
            })
            .addCase(reviewSponsorshipInterest.fulfilled, (state, action) => {
                state.isUpdating = false;
            })
            .addCase(reviewSponsorshipInterest.rejected, (state, action) => {
                state.isUpdating = false;
                state.sponsorshipsError = action.payload?.message || "Failed to review sponsorship interest";
            })

            // ==========================================
            // FETCH MODERATORS
            // ==========================================
            .addCase(fetchModerators.pending, (state) => {
                state.isLoadingModerators = true;
                state.moderatorsError = null;
            })
            .addCase(fetchModerators.fulfilled, (state, action) => {
                state.isLoadingModerators = false;
                state.moderators = action.payload.data?.moderators || [];
            })
            .addCase(fetchModerators.rejected, (state, action) => {
                state.isLoadingModerators = false;
                state.moderatorsError = action.payload?.message || "Failed to fetch moderators";
            })

            // ==========================================
            // PROMOTE TO MODERATOR
            // ==========================================
            .addCase(promoteToModerator.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(promoteToModerator.fulfilled, (state) => {
                state.isUpdating = false;
            })
            .addCase(promoteToModerator.rejected, (state, action) => {
                state.isUpdating = false;
                state.moderatorsError = action.payload?.message || "Failed to promote user";
            })

            // ==========================================
            // UPDATE MODERATOR PERMISSIONS
            // ==========================================
            .addCase(updateModeratorPermissions.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(updateModeratorPermissions.fulfilled, (state, action) => {
                state.isUpdating = false;
                const updated = action.payload.data?.user;
                if (updated) {
                    const idx = state.moderators.findIndex(m => m._id === updated._id);
                    if (idx !== -1) {
                        state.moderators[idx].moderator_permissions = updated.moderator_permissions;
                    }
                }
            })
            .addCase(updateModeratorPermissions.rejected, (state, action) => {
                state.isUpdating = false;
                state.moderatorsError = action.payload?.message || "Failed to update permissions";
            })

            // ==========================================
            // REVOKE MODERATOR ROLE
            // ==========================================
            .addCase(revokeModeratorRole.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(revokeModeratorRole.fulfilled, (state, action) => {
                state.isUpdating = false;
                state.moderators = state.moderators.filter(m => m._id !== action.payload.userId);
            })
            .addCase(revokeModeratorRole.rejected, (state, action) => {
                state.isUpdating = false;
                state.moderatorsError = action.payload?.message || "Failed to revoke moderator role";
            })

            // ==========================================
            // FETCH AUDIT LOGS
            // ==========================================
            .addCase(fetchAuditLogs.pending, (state) => {
                state.isLoadingAuditLogs = true;
                state.auditLogsError = null;
            })
            .addCase(fetchAuditLogs.fulfilled, (state, action) => {
                state.isLoadingAuditLogs = false;
                
                const isAppending = !!action.meta.arg.cursor;
                const newLogs = action.payload.data?.logs || action.payload.data || [];
                
                if (isAppending) {
                    state.auditLogs = [...state.auditLogs, ...newLogs];
                } else {
                    state.auditLogs = newLogs;
                }
                
                const pagination = action.payload.data?.pagination || action.payload.pagination || {};
                state.auditLogsHasNext = pagination.hasNext || false;
                state.auditLogsCursor = pagination.cursor || null;
            })
            .addCase(fetchAuditLogs.rejected, (state, action) => {
                state.isLoadingAuditLogs = false;
                state.auditLogsError = action.payload?.message || "Failed to fetch audit logs";
            })

            // ==========================================
            // FETCH EVENT BY ID (audit nav deep-link)
            // ==========================================
            .addCase(fetchAdminEventById.pending, (state) => {
                state.isLoadingAuditNav = true;
                state.auditNavEntity = null;
                state.auditNavError = null;
            })
            .addCase(fetchAdminEventById.fulfilled, (state, action) => {
                state.isLoadingAuditNav = false;
                state.auditNavEntity = action.payload.data?.event || null;
            })
            .addCase(fetchAdminEventById.rejected, (state, action) => {
                state.isLoadingAuditNav = false;
                state.auditNavError = action.payload?.message || "Event not found";
            })

            // ==========================================
            // FETCH USER BY ID (audit nav deep-link)
            // ==========================================
            .addCase(fetchAdminUserById.pending, (state) => {
                state.isLoadingAuditNav = true;
                state.auditNavEntity = null;
                state.auditNavError = null;
            })
            .addCase(fetchAdminUserById.fulfilled, (state, action) => {
                state.isLoadingAuditNav = false;
                state.auditNavEntity = action.payload.data?.user || null;
            })
            .addCase(fetchAdminUserById.rejected, (state, action) => {
                state.isLoadingAuditNav = false;
                state.auditNavError = action.payload?.message || "User not found";
            });
    },
});

export const { clearError, clearCurrentRoute, setAppliedFilters, clearAuditNav } = adminSlice.actions;
export default adminSlice.reducer;
